import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

async function fetchRequirementDefectRatioBreakdown(breakdown, queryString) {
  const url = `${QUALITY_SERVICE_API_URL}/requirement-defect-ratio/breakdown?breakdown=${breakdown}${queryString ? `&${queryString}` : ''}`;
  console.log('[useRequirementDefectRatioBreakdown] Fetching from:', url);
  try {
    const response = await api.get(url);
    console.log('[useRequirementDefectRatioBreakdown] Success:', response.data);
    return response.data;
  } catch (err) {
    console.error('[useRequirementDefectRatioBreakdown] Error:', {
      status: err.response?.status,
      statusText: err.response?.statusText,
      data: err.response?.data,
      message: err.message,
    });
    throw err;
  }
}

function useRequirementDefectRatioBreakdown(breakdownType, filters, dateRange) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Default to last 30 days if no dateRange provided
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const effectiveDateRange = dateRange || { startDate: thirtyDaysAgo, endDate: now };
  
  const params = new URLSearchParams();
  ['domain', 'group', 'unit'].forEach(key => {
    (filters?.[key] || []).forEach(value => params.append(key, value));
  });
  const formatDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  if (effectiveDateRange?.startDate) params.set('from', formatDate(effectiveDateRange.startDate));
  if (effectiveDateRange?.endDate) params.set('to', formatDate(effectiveDateRange.endDate));
  const queryString = params.toString();

  const fetchBreakdown = useCallback(async (isCancelled = () => false) => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const breakdown = await fetchRequirementDefectRatioBreakdown(breakdownType, queryString);
      if (!isCancelled()) setData(breakdown);
    } catch (err) {
      if (!isCancelled()) setError(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [breakdownType, queryString]);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;
    fetchBreakdown(isCancelled);
    return () => { cancelled = true; };
  }, [fetchBreakdown]);

  const refetch = useCallback(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;
    fetchBreakdown(isCancelled);
    return () => { cancelled = true; };
  }, [fetchBreakdown]);

  return { data, loading, error, refetch };
}

export default useRequirementDefectRatioBreakdown;
